import { styled } from "goober";
import { useCallback, useEffect, useState } from "react";
import { formatBytes } from "../../lib/formatBytes";
import { Button } from "../Button";
import { CircleMesh } from "../CircleMesh";
import { metaJoin } from "../MetaSeparator";
import { Text } from "../Text";
import { extensionFromMime, triggerDownload } from "./download";

const Container = styled("div")`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  width: 100%;
  height: 100%;
`;

const Background = styled("div")`
  position: absolute;
  inset: 0;
`;

const Card = styled("div")`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 24px 32px;
  border-radius: 12px;
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  background: var(--alpha4);
`;

const BadgeRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Badge = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
  height: 20px;
  background: var(--alpha4);
  border-radius: 38px;
  color: var(--text-secondary);
`;

type Props = {
  url?: string;
  contentType: string;
  fileName?: string;
  bleed?: number;
};

export function Binary({ url, contentType, fileName, bleed }: Props) {
  const [fileSize, setFileSize] = useState<number>();
  const [downloading, setDownloading] = useState(false);

  const apiUrl = url;

  useEffect(() => {
    if (!apiUrl) return;
    const controller = new AbortController();
    void fetch(apiUrl, {
      credentials: "include",
      method: "HEAD",
      signal: controller.signal,
    })
      .then((res) => {
        if (res.ok) {
          const size = Number(res.headers.get("Content-Length"));
          if (size > 0) setFileSize(size);
        }
      })
      .catch(() => {});
    return () => controller.abort();
  }, [apiUrl]);

  const ext = extensionFromMime(contentType);
  const downloadName = fileName ?? `output.${ext}`;

  const download = useCallback(async () => {
    if (!apiUrl || downloading) return;
    setDownloading(true);
    try {
      const res = await fetch(apiUrl, { credentials: "include" });
      const url = URL.createObjectURL(await res.blob());
      triggerDownload(url, downloadName);
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }, [apiUrl, downloadName, downloading]);

  const parts = [ext.toUpperCase()];
  if (fileSize != null) parts.push(formatBytes(fileSize));

  return (
    <Container
      style={
        bleed
          ? {
              margin: -bleed,
              width: `calc(100% + ${bleed * 2}px)`,
              height: `calc(100% + ${bleed * 2}px)`,
            }
          : undefined
      }
    >
      <Background>
        <CircleMesh mutingIntensity={1} seed="binary-preview" />
      </Background>
      <Card>
        <Text variant="text-regular-md" color="var(--text-primary)">
          {extensionFromMime(contentType).toUpperCase()} file
        </Text>
        <Button
          variant="secondary"
          icon="arrow-down"
          onClick={download}
          disabled={downloading}
        >
          {downloading ? "Downloading…" : "Download"}
        </Button>
        <BadgeRow>
          <Badge>
            <Text variant="text-small-md">{metaJoin(parts)}</Text>
          </Badge>
        </BadgeRow>
      </Card>
    </Container>
  );
}
